import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {Tool} from 'common/src/modules/shared/copilot/copilot.types';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {z, ZodTypeAny} from 'zod';
import {cardTypeToName} from '../../campaignSetupAgent.config';
import getSetupAgentModel from '../../utils/getSetupAgentModel';
import getPromptToCardAgentInstructions from './utils/getPromptToCardAgentInstructions';

export enum PromptToCardAgentStatus {
  AddOrUpdate = 'addOrUpdate',
  NoAction = 'noAction',
}

/**
 * An helper function to get a standlonme agent to support translating from a prompt to a specific card schema
 * @param config {
  cardType: The type of the card to translate to
  cardId: an existing card id to update or 0 to create a new card
  tools: list of tools that will be used to complete parts of the card schema, such as creative, feed and more
  cardSchema: The schema of the card to translate to
  cardSpecificInstructions?: The specific instructions for the card to translate to
}
 * @returns an object containing the chain of thoughts (what was the agent doing), status (addOrUpdate or noAction) and the card schema
 */
export default function promptToCardAgent({
  cardId,
  cardType,
  tools,
  cardSpecificInstructions,
  cardSchema,
}: {
  cardType: NodeType;
  cardId: number;
  tools: Tool<ZodTypeAny>[];
  cardSchema: ZodTypeAny;
  cardSpecificInstructions?: string;
}) {
  return standaloneAgent({
    name: `promptTo${cardTypeToName[cardType]}CardAgent`,
    role: `Your purpose is to convert a natural language prompt to a ${cardTypeToName[cardType]} card`,
    instructions: getPromptToCardAgentInstructions({
      cardType,
      cardId,
      cardSpecificInstructions,
    }),
    tools,
    responseSchema: z.object({
      status: z.enum([PromptToCardAgentStatus.AddOrUpdate, PromptToCardAgentStatus.NoAction]),
      chainOfThoughts: z
        .string()
        .describe(
          `What was done. The summary of actions. Which issues did you experience. Suggestions to user. Questions to user.`,
        ),
      data: cardSchema,
    }),
    model: getSetupAgentModel(),
  });
}
